import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getArticleBySlug, getArticleById, getRelatedArticles } from '@/mocks/articles';
import type { AnimeArticle } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<AnimeArticle | undefined>(undefined);
  const [related, setRelated] = useState<AnimeArticle[]>([]);
  const [showAllEpisodes, setShowAllEpisodes] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!id) return;
    let found = getArticleBySlug(id);
    if (!found) {
      const numId = parseInt(id, 10);
      if (!isNaN(numId)) found = getArticleById(numId);
    }
    setArticle(found);
    setShowAllEpisodes(false);
    if (found) {
      setRelated(getRelatedArticles(found, 4));
      document.title = `${found.title} | ${found.alias} - 51动漫在线观看`;
      document.querySelector('meta[name="description"]')?.setAttribute('content', found.description.slice(0, 150));
      document.querySelector('meta[name="keywords"]')?.setAttribute('content', `51动漫,${found.title},${found.alias},${found.tags.join(',')},国产动漫在线观看`);
    }
  }, [id]);

  useEffect(() => {
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) existingScript.remove();
    if (!article) return;

    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://heiliaochigua.cn/' },
            { '@type': 'ListItem', position: 2, name: article.category, item: `http://heiliaochigua.cn/category/${article.categorySlug}` },
            { '@type': 'ListItem', position: 3, name: article.title, item: `http://heiliaochigua.cn/article/${article.slug}` },
          ],
        },
        {
          '@type': 'Article',
          headline: `${article.title} - ${article.alias}`,
          description: article.description,
          author: { '@type': 'Person', name: article.author },
          publisher: { '@type': 'Organization', name: '51动漫' },
          datePublished: '2024-01-01',
          dateModified: article.episodes[0]?.date || '2026-07-01',
          image: article.image,
          url: `http://heiliaochigua.cn/article/${article.slug}`,
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);
  }, [article]);

  if (!article) {
    return (
      <main className="min-h-screen pt-20 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-12 py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading text-foreground-950 mb-2">未找到该动漫</h1>
          <p className="text-sm text-foreground-500 mb-6">该动漫详情不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const displayedEpisodes = showAllEpisodes
    ? article.episodes
    : article.episodes.slice(0, 10);

  const renderStars = (rating: number) => {
    const full = Math.floor(rating);
    const stars = [];
    for (let i = 0; i < full; i++) {
      stars.push(<i key={`f-${i}`} className="ri-star-fill text-secondary-500 text-sm"></i>);
    }
    const remaining = 5 - full;
    for (let i = 0; i < remaining; i++) {
      stars.push(<i key={`e-${i}`} className="ri-star-line text-foreground-300 text-sm"></i>);
    }
    return stars;
  };

  return (
    <main className="min-h-screen">
      {/* Banner */}
      <section className="relative w-full h-[280px] md:h-[380px] overflow-hidden">
        <img
          src={article.bannerImage}
          alt={`${article.title} 横幅`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60"></div>
      </section>

      {/* Content */}
      <section className="w-full px-4 md:px-6 lg:px-12 -mt-20 relative z-10 pb-14">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-background-100/70 mb-6" aria-label="面包屑导航">
          <Link to="/" className="hover:text-background-50 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <Link to={`/category/${article.categorySlug}`} className="hover:text-background-50 transition-colors">{article.category}</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-background-100 font-medium">{article.title}</span>
        </nav>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left column - Info */}
          <div className="lg:w-[65%]">
            <div className="bg-background-50 rounded-lg p-5 md:p-8">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                  {article.status}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-1">
                {article.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">{article.alias}</p>

              <div className="flex items-center gap-1.5 mb-6">
                {renderStars(article.rating)}
                <span className="text-lg font-heading text-foreground-950 ml-2">{article.rating}</span>
                <span className="text-xs text-foreground-400 ml-1">/ 5.0</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6 p-4 rounded-lg bg-background-100">
                <div>
                  <p className="text-xs text-foreground-500">制作平台</p>
                  <p className="text-sm font-medium text-foreground-800 mt-0.5">{article.studio}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">原作作者</p>
                  <p className="text-sm font-medium text-foreground-800 mt-0.5">{article.author}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">更新时间</p>
                  <p className="text-sm font-medium text-foreground-800 mt-0.5">{article.updateDay} {article.updateTime}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">总集数</p>
                  <p className="text-sm font-medium text-foreground-800 mt-0.5">{article.totalEpisodes} 集</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">更新至</p>
                  <p className="text-sm font-medium text-primary-600 mt-0.5">第 {article.currentEpisode} 集</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">评分人数</p>
                  <p className="text-sm font-medium text-foreground-800 mt-0.5">{(article.rating * 1247).toLocaleString()} 人</p>
                </div>
              </div>

              <h2 className="text-lg font-heading text-foreground-950 mb-3">剧情简介</h2>
              <div className="prose prose-sm max-w-none">
                <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                  {article.longDescription}
                </p>
              </div>
            </div>

            {/* Episodes */}
            <div className="mt-6 bg-background-50 rounded-lg p-5 md:p-8">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-heading text-foreground-950">
                  剧集列表
                  <span className="text-sm text-foreground-500 font-normal ml-2">
                    (共 {article.totalEpisodes} 集 / 更新至 {article.currentEpisode} 集)
                  </span>
                </h2>
                <span className="text-xs text-accent-600 font-medium">
                  <i className="ri-time-line mr-1"></i>
                  每{article.updateDay} {article.updateTime} 更新
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {displayedEpisodes.map((ep) => (
                  <div
                    key={ep.num}
                    className="flex flex-col p-2.5 rounded-lg border border-background-200 hover:border-primary-300 hover:bg-primary-50/50 transition-colors cursor-pointer"
                  >
                    <span className="text-xs font-medium text-foreground-800 truncate">第{ep.num}集</span>
                    <span className="text-xs text-foreground-500 truncate mt-0.5">{ep.title.replace(/^第\d+集\s*/, '')}</span>
                    <span className="text-xs text-foreground-400 mt-1">{ep.date}</span>
                  </div>
                ))}
              </div>

              {article.episodes.length > 10 && (
                <button
                  onClick={() => setShowAllEpisodes(!showAllEpisodes)}
                  className="mt-5 w-full py-2.5 rounded-lg border border-background-200 text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {showAllEpisodes ? '收起剧集' : `查看全部 ${article.episodes.length} 集`}
                  {showAllEpisodes ? <i className="ri-arrow-up-s-line ml-1"></i> : <i className="ri-arrow-down-s-line ml-1"></i>}
                </button>
              )}
            </div>
          </div>

          {/* Right column - Sidebar */}
          <div className="lg:w-[35%]">
            <div className="bg-background-50 rounded-lg p-5 md:p-6 sticky top-20">
              <div className="w-full h-[220px] rounded-lg overflow-hidden mb-5">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`${article.title} - 51动漫`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <Link
                to={`/category/${article.categorySlug}`}
                className="block w-full text-center py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors mb-3 whitespace-nowrap"
              >
                立即观看
              </Link>

              <Link
                to={`/category/${article.categorySlug}`}
                className="block w-full text-center py-3 rounded-lg border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                查看同分类动漫
              </Link>

              <div className="mt-6 pt-5 border-t border-background-200">
                <h3 className="text-sm font-heading text-foreground-950 mb-3">更新动态</h3>
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-accent-600 font-medium">{article.updateDay}</span>
                    <span className="text-foreground-500">定期更新</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-foreground-400">{article.updateTime}</span>
                    <span className="text-foreground-500">准时发布</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-primary-600 font-medium">第 {article.currentEpisode} 集</span>
                    <span className="text-foreground-500">最新一集</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-14">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((item) => (
                <Link
                  key={item.slug}
                  to="/category/all"
                  className="group rounded-lg overflow-hidden bg-background-50 hover:bg-background-50/80 transition-colors"
                >
                  <div className="relative w-full h-[160px] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      title={`${item.title} - 相关推荐`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 right-2 flex items-center gap-0.5 bg-background-950/70 backdrop-blur-sm px-1.5 py-0.5 rounded-full">
                      <span className="text-background-50 text-xs font-medium">{item.rating}</span>
                      <i className="ri-star-fill text-secondary-500 text-xs"></i>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-heading text-foreground-950 group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">更新至 {item.currentEpisode} 集</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </section>
    </main>
  );
}